import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useParams} from 'react-router-dom'
import {toast} from 'sonner'
import NetworkService from '../service/network.services'
import ActivityService from '../service/activity.services'
import {
  TNetwork,
  TNetworkWithCounts,
  TCreateNetworkInput,
  TUpdateNetworkInput,
  TUpdateNetworkPreferencesInput,
  TResolvedSection,
} from '../types/network.type'
import {
  TNetworkActivitiesFilters,
  TActivitiesResponse,
} from '../types/project.type'
import {TViewportResponse, TViewportWindow} from '../types/viewport.type'
import {ErrorHelper} from '../types/app.type'
import {getTranslation} from '../utils/getTranslation'
import {TPkEnvelope} from '../utils/sectorWindow.functions'
import type {TNetworkGeoZones} from '../types/geoZones.type'
import {
  PROJECT_WEEK_QUERY_KEY,
  useProjectWeekActions,
  WeekEntities,
} from '../store/domain/projectWeek.slice'

const networkKeys = {
  list: (clientId: string) => ['network', 'listForClient', clientId] as const,
  // Network-scoped cache: keyed by networkId so two projects in the same
  // network share the cached response.
  withGeoZones: (networkId: string) =>
    ['network', 'withGeoZones', networkId] as const,
}

// Network-scoped, flat geo-zones query — resolved straight from the networkId.
// Shares the ['network', 'withGeoZones'] cache key with the invalidations in
// access.hooks / geoMarker.hooks.
export const useGetNetworkGeoZones = (networkId: string | undefined) => {
  const {data, isLoading} = useQuery<TNetworkGeoZones, ErrorHelper>({
    queryKey: networkKeys.withGeoZones(networkId ?? ''),
    queryFn: () => NetworkService.getNetworkGeoZones(networkId as string),
    enabled: Boolean(networkId),
    // Reference data: invalidated by access/geo-marker create/update/delete.
    staleTime: Infinity,
  })
  return {data, isLoading}
}

export const useNetworksForClient = (clientId: string | undefined) => {
  const {data, isPending, isLoading, isSuccess, error} = useQuery<
    TNetworkWithCounts[],
    ErrorHelper
  >({
    queryKey: networkKeys.list(clientId ?? ''),
    queryFn: () => NetworkService.listNetworksForClient(clientId as string),
    enabled: Boolean(clientId),
    // Reference data: invalidated by create/update/delete/moveProject mutations below.
    staleTime: Infinity,
  })

  return {data, isPending, isLoading, isSuccess, error}
}

export const useNetworkDefaultPkRange = (
  networkId: string,
): TPkEnvelope | null => {
  const {clientId} = useParams()
  const {data: networks} = useNetworksForClient(clientId)
  const network = networks?.find(n => n.id === networkId)
  return useMemo(() => {
    if (
      !network ||
      network.defaultPkStart === null ||
      network.defaultPkEnd === null
    ) {
      return null
    }
    return {kmStart: network.defaultPkStart, kmEnd: network.defaultPkEnd}
  }, [network])
}

export const useCreateNetwork = (clientId: string) => {
  const qc = useQueryClient()
  const {
    mutate: createNetwork,
    isPending,
    isSuccess,
    error,
  } = useMutation<TNetwork, ErrorHelper, TCreateNetworkInput>({
    mutationFn: (input: TCreateNetworkInput) =>
      NetworkService.createNetwork(input),
    onSuccess: () => {
      qc.invalidateQueries({queryKey: networkKeys.list(clientId)})
      toast.success(getTranslation('network:createdSuccess'))
    },
    onError: err => {
      toast.error(getTranslation('network:createdError'), {
        description: err.message,
      })
    },
  })
  return {createNetwork, isPending, isSuccess, error}
}

export const useUpdateNetwork = (clientId: string) => {
  const qc = useQueryClient()
  const {
    mutate: updateNetwork,
    isPending,
    isSuccess,
    error,
  } = useMutation<
    TNetwork,
    ErrorHelper,
    {id: string; input: TUpdateNetworkInput}
  >({
    mutationFn: ({id, input}) => NetworkService.updateNetwork(id, input),
    onSuccess: () => {
      qc.invalidateQueries({queryKey: networkKeys.list(clientId)})
      toast.success(getTranslation('network:updatedSuccess'))
    },
    onError: err => {
      toast.error(getTranslation('network:updatedError'), {
        description: err.message,
      })
    },
  })
  return {updateNetwork, isPending, isSuccess, error}
}

/**
 * Saves the network's default linear-view window (Preferences tab).
 * Invalidates the networks list so the editor's own resolution picks the new
 * default up immediately; other users get it on their next session load —
 * acceptable for a preference.
 */
export const useUpdateNetworkPreferences = (clientId: string) => {
  const qc = useQueryClient()
  const {
    mutate: updatePreferences,
    isPending,
    error,
  } = useMutation<
    TNetwork,
    ErrorHelper,
    {networkId: string; input: TUpdateNetworkPreferencesInput}
  >({
    mutationFn: ({networkId, input}) =>
      NetworkService.updateNetworkPreferences(networkId, input),
    onSuccess: () => {
      qc.invalidateQueries({queryKey: networkKeys.list(clientId)})
      toast.success(getTranslation('linearView:preferencesSavedSuccess'))
    },
    onError: err => {
      toast.error(getTranslation('linearView:preferencesSavedError'), {
        description: err.message,
      })
    },
  })
  return {updatePreferences, isPending, error}
}

export const useDeleteNetwork = (clientId: string) => {
  const qc = useQueryClient()
  const {
    mutate: deleteNetwork,
    isPending,
    isSuccess,
    error,
  } = useMutation<void, ErrorHelper, string>({
    mutationFn: (id: string) => NetworkService.softDeleteNetwork(id),
    onSuccess: () => {
      qc.invalidateQueries({queryKey: networkKeys.list(clientId)})
      toast.success(getTranslation('network:deletedSuccess'))
    },
    onError: err => {
      toast.error(getTranslation('network:deletedError'), {
        description: err.message,
      })
    },
  })
  return {deleteNetwork, isPending, isSuccess, error}
}

/**
 * Resolves the trajectory between two PKs of a network into per-track (or
 * per-project) sections. Moved here from junction.hooks.ts in Lot 2b, when the
 * endpoint became network-scoped.
 *
 * Read-only, but a mutation on purpose: it is triggered by an explicit button
 * in the demand form, never on mount. No cache to invalidate.
 */
/**
 * The PK viewport of the linear view. Blocks come back already ordered and the
 * notions already carry their resolved tracks — nothing is derived here.
 */
export const useGetViewport = (
  networkId: string | undefined,
  window: TViewportWindow | undefined,
  trackIds: string[] = [],
) => {
  const {data, isLoading, error} = useQuery<TViewportResponse, ErrorHelper>({
    queryKey: ['network', 'viewport', networkId, window, trackIds],
    queryFn: () =>
      NetworkService.getViewport(
        networkId as string,
        window as TViewportWindow,
        trackIds,
      ),
    enabled: Boolean(networkId) && window !== undefined,
    staleTime: Infinity,
  })
  return {data, isLoading, error}
}

export const useResolvePath = () => {
  const {
    mutate: resolvePath,
    isPending,
    data,
    reset,
  } = useMutation<
    {sections: TResolvedSection[]},
    ErrorHelper,
    {
      networkId: string
      pkStart: number
      pkEnd: number
      granularity?: 'track' | 'km'
    }
  >({
    mutationFn: ({networkId, pkStart, pkEnd, granularity}) =>
      NetworkService.resolvePath(networkId, pkStart, pkEnd, granularity),
  })
  return {resolvePath, isPending, data, reset}
}

/**
 * Shared plumbing behind `useWeekData`: runs the week-bundle query and wires it
 * into the projectWeek store (loading source-of-truth reset, dedup'd store push,
 * error toast). The caller supplies the queryKey/queryFn/enabled for its own
 * window (PK range or sector).
 */
const useWeekDataQuery = <TData extends Partial<WeekEntities>>(params: {
  queryKey: unknown[]
  queryFn: () => Promise<TData>
  enabled: boolean
}) => {
  const setFromWeekData = useProjectWeekActions(s => s.setFromWeekData)

  // Track previous data to prevent unnecessary updates
  const previousDataRef = useRef<TData | null>(null)

  const {data, isLoading, isRefetching, refetch, isError, error} = useQuery<
    TData | undefined,
    ErrorHelper
  >({
    queryKey: params.queryKey,
    queryFn: params.queryFn,
    enabled: params.enabled,
    // Volatile bucket: the week bundle (activities, notices, approvals,
    // conflicts) moves on every OPC/Requestor mutation, so refetch on every
    // mount/key change. Mutations invalidate PROJECT_WEEK_QUERY_KEY.
    staleTime: 0,
  })

  // Surface a toast when the week-data fetch ultimately fails, so the user
  // doesn't sit in front of an empty content area without any feedback
  useEffect(() => {
    if (!isError) return
    toast.error(getTranslation('project:weekDataError'), {
      description: error?.message,
    })
  }, [isError, error])

  // Make the projectWeek store the single source of truth for "loading":
  // clear all week-scoped collections as soon as the queryKey changes, so
  // every consumer (views, conflicts panel, validation buttons) immediately
  // sees an empty state during the fetch instead of stale data from the
  // previous week
  useEffect(() => {
    setFromWeekData({
      activities: [],
      movements: [],
      notices: [],
      approvals: [],
      conflicts: [],
    })
    // Reset the dedup ref so the next data arrival is pushed to the store
    previousDataRef.current = null
  }, [JSON.stringify(params.queryKey), setFromWeekData])

  const updateWeekData = useCallback(
    (weekData: TData) => {
      setFromWeekData({
        activities: weekData.activities ?? [],
        notices: weekData.notices ?? [],
        movements: weekData.movements ?? [],
        approvals: weekData.approvals ?? [],
        conflicts: weekData.conflicts ?? [],
      })
    },
    [setFromWeekData],
  )

  useEffect(() => {
    // Skip if no data
    if (!data) return

    // Deep comparison to detect actual changes in content
    const hasChanged =
      !previousDataRef.current ||
      JSON.stringify(previousDataRef.current) !== JSON.stringify(data)

    if (!hasChanged) return

    // Update reference
    previousDataRef.current = data

    // Update store
    updateWeekData(data)
  }, [data, updateWeekData])

  return {data, isLoading, isRefetching, refetch}
}

/**
 * The week bundle feeding the work screens, sourced from the network viewport
 * (`/viewport/week`) over a discriminated window — either the project's own PK
 * bounds (a single block, so the axis is indistinguishable from the legacy
 * project axis, plan 2d §9.5) or the selected sectors (resolved server-side to
 * one chemin over the union of their segments).
 *
 * The query key STAYS under `PROJECT_WEEK_QUERY_KEY`: the eight mutation
 * invalidations target that prefix (§4.4), so keeping the key beneath it means
 * they keep working untouched. Changing the prefix would freeze the screen after
 * every mutation.
 */
export const useWeekData = (
  networkId: string,
  window: TViewportWindow | undefined,
  weekNumber: string,
  year: string,
) =>
  useWeekDataQuery({
    queryKey: [...PROJECT_WEEK_QUERY_KEY, networkId, window, weekNumber, year],
    queryFn: () =>
      NetworkService.getViewportWeek(
        networkId,
        window as TViewportWindow,
        weekNumber,
        year,
      ),
    enabled: !!networkId && window !== undefined && !!weekNumber && !!year,
  })

export const useNetworkActivities = (
  networkId: string,
  filters?: TNetworkActivitiesFilters,
  page: number = 1,
  perPage?: number,
  enabled: boolean = true,
) => {
  const {data, isLoading, isRefetching, refetch} = useQuery<
    TActivitiesResponse | undefined,
    ErrorHelper
  >({
    queryKey: ['networkActivities', networkId, filters, page, perPage],
    queryFn: () =>
      ActivityService.getNetworkActivities(networkId, filters, page, perPage),
    // `enabled` lets callers suppress the query — e.g. the activity list in
    // period mode before a date range is picked, where firing without a date
    // filter would fetch the whole client.
    enabled: !!networkId && enabled,
    // Volatile bucket: activities change continuously via OPC/Requestor
    // mutations, so no staleTime (refetch on every mount/key change).
    staleTime: 0,
  })

  return {data, isLoading, isRefetching, refetch}
}
